
import {defineComponent, PropType} from "vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import DictionaryNameField from "@/components/molecules/DictionaryNameField.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import gql from "graphql-tag";
import {Controller} from "@/store/modules/hcs/controller/model";
import {Dictionary} from "@/store/modules/hds/Model";
import natsort from "natsort";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import {AxiosInstance} from "axios";
import {buildParamHash} from "@/store/modules/hcs/controller/actions";
import {ServiceImplementation} from "@/store/modules/hcs/serviceImplementation/model";
import {DataImplementation} from "@/store/modules/hcs/dataImplementation/model";
import ErrorMessage from "@/components/molecules/ErrorMessage";
import ErrorPanel from "@/components/molecules/ErrorPanel.vue";
import ErrorPopover from "@/components/molecules/ErrorPopover.vue";

const sortFn = natsort();

interface DictionaryView extends Dictionary {
  errors: ErrorMessage[];
}

export default defineComponent({
  name: 'OmakaseImplementationDialog',
  components: {ErrorPopover, ErrorPanel, TextFieldLike, TableInline, DictionaryNameField, ModelSelect, SimpleDialog},
  props: {
    value: {
      required: true,
    },
    initialControllerId: {
      type: [String, null] as PropType<string | null>,
      required: false,
      default: null,
    }
  },
  emits: [
    'input', 'close', 'apply', 'done:save'
  ],
  data() {
    return {
      controllerId: this.initialControllerId,
      controllers: [] as Controller[],
      dataImplementations: [] as DataImplementation[],
      serviceImplementations: [] as ServiceImplementation[],
      dictionaries: [] as Dictionary[],
      selectedDictionary: null as DictionaryView | null,
    };
  },
  computed: {
    ...mapGetters({
      hdsClient: 'hds/gqlClient',
      _hcsClient: 'hcs/client',
      account: 'hcs/account',
    }),
    hcsClient(): AxiosInstance {
      return this._hcsClient as AxiosInstance;
    },
    dictionaryView(): DictionaryView[] {
      const h = this.$createElement;
      return this.dictionaries
        .slice()
        .map(d => {
          const errors: ErrorMessage[] = [];
          const existsDataImpls = d.dataComponentModels.flatMap(dcm => this.dataImplementations.filter(di => dcm.id === di.dataComponentModelId && this.controllerId === di.controllerId));
          if (existsDataImpls.length > 0) {
            errors.push({
              level: "error",
              message: [
                h('h4', "既に以下のデータ実装が存在するため、おまかせ実装できません。"),
                h('ul', existsDataImpls.map(di => h('li', di.dataComponentModelName)))
              ],
            });
          }
          const existsServiceImpls = d.serviceComponentModels.flatMap(scm => this.serviceImplementations.filter(si => scm.id === si.serviceComponentModelId && this.controllerId === si.controllerId));
          if (existsServiceImpls.length > 0) {
            errors.push({
              level: "error",
              message: [
                h('h4', "既に以下のサービス実装が存在するため、おまかせ実装できません。"),
                h('ul', existsServiceImpls.map(si => h('li', si.serviceComponentModelName)))
              ],
            });
          }
          return {
            ...d,
            errors,
          }
        })
        .sort((a, b) => sortFn(a.name, b.name));
    },
    isValid(): boolean {
      return this.selectedDictionary !== null && this.selectedDictionary.errors.length === 0;
    }
  },
  methods: {
    ...mapActions({
      _createServiceImplementation: 'hcs/serviceImplementation/create',
    }),
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    async load() {
      const {data: controllers} = await this.hcsClient.get<Controller[]>('/controllers', buildParamHash({
        q: { terminalIdEq: this.account.user?.terminal?.id }
      }));
      this.controllers = controllers;

      const {data: serviceImplementations} = await this.hcsClient.get<ServiceImplementation[]>('/service_implementations');
      this.serviceImplementations = serviceImplementations;

      const {data: dataImplementations} = await this.hcsClient.get<DataImplementation[]>('/data_implementations');
      this.dataImplementations = dataImplementations;

      const {data: hdsResponse} = await this.hdsClient.query({
          query: gql`
            query OmakaseImplementationDialog {
                dictionary: dictionariesByTypes(dictionaryTypes: [local, external]) {
                    id
                    name
                    description
                    dictionaryType
                    dataComponentModels {
                        id
                        name
                    }
                    serviceComponentModels {
                        id
                        name
                    }
                }
            }`
        });
      this.dictionaries = hdsResponse.dictionary;
    },
    async save() {
      const dictionary = this.selectedDictionary;
      if(dictionary === null) {
        return;
      }

      const loading = this.$loading({
        lock: true,
        text: `辞書「${dictionary.name}」でおまかせ実装を行っています...`,
        spinner: 'el-icon-loading',
      });
      try {
        await this.hcsClient.post(`/controllers/${this.controllerId}/dictionary_implementations`, {
          dictionaryId: dictionary.id,
        });
        this.showSnackbar({
          message: `辞書「${dictionary.name}」のおまかせ実装が完了しました。`,
        });
        this.$emit('done:save');
      } catch (e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `辞書「${dictionary.name}」のおまかせ実装に失敗しました。`,
        });
      } finally {
        loading.close();
      }
    },
    handleDictionarySelectionChange(row: DictionaryView) {
      // @ts-ignore
      this.selectedDictionary = row;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value, oldValue) {
        if(value && !oldValue) {
          this.load();
        }
      }
    }
  }
})
